import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  ClipboardList, Plus, Search, Filter, ChevronDown, ChevronRight,
  AlertTriangle, Clock, CheckCircle2, XCircle, Edit3, Trash2,
  Send, User, Building2, Calendar, Tag, Shield, Bell, BellOff,
  X, Save, Mail, Phone, Loader2, SlidersHorizontal, ArrowUpDown,
  FileText, AlertCircle, CheckCheck, Circle
} from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from '../utils/api';
import { Widget } from './Common';
import toast from 'react-hot-toast';

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  open:          { label: 'Open',          color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: AlertCircle },
  in_progress:   { label: 'In Progress',   color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: Clock },
  draft:         { label: 'Draft',         color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', icon: Circle },
  completed:     { label: 'Completed',     color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: CheckCircle2 },
  unachievable:  { label: 'Unachievable',  color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: XCircle },
};

const PRIORITY_CONFIG = {
  High:   { color: '#dc2626', bg: '#fef2f2' },
  Medium: { color: '#d97706', bg: '#fffbeb' },
  Low:    { color: '#16a34a', bg: '#f0fdf4' },
};

const STATUS_ORDER = ['open', 'in_progress', 'draft', 'completed', 'unachievable'];

const REMINDER_OPTIONS = [
  { value: null,  label: 'No reminder' },
  { value: 1,    label: '1 day before' },
  { value: 7,    label: '7 days before' },
  { value: 14,   label: '14 days before' },
  { value: 30,   label: '30 days before' },
];

const NIMS_CAPABILITIES = [
  'General', 'Operational Coordination', 'Mass Care Services',
  'Public Information and Warning', 'Operational Communications',
  'Public Health and Medical Services', 'Logistics and Supply Chain Management',
  'Situational Assessment', 'Critical Transportation', 'Infrastructure Systems',
  'Cybersecurity', 'Planning', 'Community Resilience',
];

const POETE_CATEGORIES = ['Planning', 'Organization', 'Equipment', 'Training', 'Exercises'];

// ── Helpers ──────────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_API_ENDPOINT || '';

const apiFetch = async (path, organization_id, options = {}) => {
  return fetchWithAuth(path, organization_id, options);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  } catch { return dateStr; }
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || status === 'completed' || status === 'cancelled') return false;
  return new Date(dueDate) < new Date();
};

const daysUntilDue = (dueDate) => {
  if (!dueDate) return null;
  const diff = new Date(dueDate) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ── Status Badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status, small }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '2px 8px' : '3px 10px',
      borderRadius: 20, fontSize: small ? 11 : 12, fontWeight: 600,
      color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.border}`,
    }}>
      <Icon size={small ? 10 : 12} />
      {cfg.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.Medium;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      color: cfg.color, background: cfg.bg,
    }}>
      {priority}
    </span>
  );
};

// ── CA Card ───────────────────────────────────────────────────────────────────

const CACard = ({ ca, onEdit, onDelete }) => {
  const overdue = isOverdue(ca.due_date, ca.status);
  const days = daysUntilDue(ca.due_date);
  const hasPendingUpdate = ca.recipient_update_status === 'pending_review';

  return (
    <div
      onClick={() => onEdit(ca)}
      style={{
        background: hasPendingUpdate ? '#fffbeb' : '#fff',
        border: `1px solid ${hasPendingUpdate ? '#fde68a' : overdue ? '#fecaca' : '#e2e8f0'}`,
        borderLeft: `4px solid ${hasPendingUpdate ? '#d97706' : overdue ? '#dc2626' : STATUS_CONFIG[ca.status]?.color || '#6366f1'}`,
        borderRadius: 8,
        padding: '14px 16px',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s',
        marginBottom: 8,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,40,85,0.10)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <PriorityBadge priority={ca.priority || 'Medium'} />
            {hasPendingUpdate && (
              <span style={{
                fontSize: 10, color: '#d97706', fontWeight: 700,
                background: '#fffbeb', padding: '2px 8px', borderRadius: 10,
                border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 3,
              }}>
                ⏳ RECIPIENT UPDATE PENDING
              </span>
            )}
            {ca.notification_sent && !hasPendingUpdate && (
              <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>● NOTIFIED</span>
            )}
            {overdue && (
              <span style={{
                fontSize: 10, color: '#dc2626', fontWeight: 700,
                background: '#fef2f2', padding: '1px 6px', borderRadius: 10,
              }}>OVERDUE</span>
            )}
          </div>

          <p style={{
            margin: '0 0 6px', fontSize: 14, fontWeight: 600,
            color: '#0f172a', lineHeight: 1.4,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {ca.title}
          </p>

          <p style={{
            margin: '0 0 10px', fontSize: 12.5, color: '#64748b', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {ca.description}
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {ca.nims_capability && ca.nims_capability !== 'General' && (
              <span style={{ fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Shield size={10} /> {ca.nims_capability}
              </span>
            )}
            {ca.assigned_org_name && (
              <span style={{ fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Building2 size={10} /> {ca.assigned_org_name}
              </span>
            )}
            {ca.due_date && (
              <span style={{
                fontSize: 11,
                color: overdue ? '#dc2626' : days <= 7 ? '#d97706' : '#475569',
                display: 'flex', alignItems: 'center', gap: 3,
                fontWeight: overdue || days <= 7 ? 600 : 400,
              }}>
                <Calendar size={10} />
                {overdue ? `Overdue since ${formatDate(ca.due_date)}` :
                 days === 0 ? 'Due today' :
                 days === 1 ? 'Due tomorrow' :
                 `Due ${formatDate(ca.due_date)}`}
              </span>
            )}
            {ca.incident_name && (
              <span style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 3 }}>
                <FileText size={10} /> {ca.incident_name}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
          <StatusBadge status={ca.status} small />
          <button
            onClick={e => { e.stopPropagation(); onDelete(ca); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#cbd5e1', padding: 4, borderRadius: 4,
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
            onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Status Group ──────────────────────────────────────────────────────────────

const StatusGroup = ({ status, cas, onEdit, onDelete, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;

  if (cas.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', background: 'none', border: 'none',
          cursor: 'pointer', padding: '10px 14px',
          borderRadius: 8, marginBottom: open ? 8 : 0,
          background: cfg.bg, transition: 'opacity 0.15s',
        }}
      >
        <Icon size={16} color={cfg.color} />
        <span style={{ fontSize: 13, fontWeight: 700, color: cfg.color, flex: 1, textAlign: 'left' }}>
          {cfg.label}
        </span>
        <span style={{
          fontSize: 12, fontWeight: 700, color: cfg.color,
          background: '#fff', borderRadius: 20, padding: '1px 10px',
          border: `1px solid ${cfg.border}`,
        }}>
          {cas.length}
        </span>
        {open ? <ChevronDown size={14} color={cfg.color} /> : <ChevronRight size={14} color={cfg.color} />}
      </button>

      {open && (
        <div style={{ paddingLeft: 4 }}>
          {cas.map(ca => (
            <CACard key={ca.ca_id} ca={ca} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── CA Modal ──────────────────────────────────────────────────────────────────

const CAModal = ({ ca, contacts, agencies, organization, onClose, onSave, onSendNotification, onAcceptUpdate, onDismissUpdate }) => {
  const [form, setForm] = useState({
    title:              ca.title || '',
    description:        ca.description || '',
    nims_capability:    ca.nims_capability || 'General',
    resource_type:      ca.resource_type || 'Planning',
    priority:           ca.priority || 'Medium',
    status:             ca.status || 'draft',
    status_notes:       ca.status_notes || '',
    assigned_org_name:  ca.assigned_org_name || '',
    assigned_contact_ids: ca.assigned_contact_ids || [],
    due_date:           ca.due_date || '',
    reminder_days:      ca.reminder_days ?? null,
    overdue_notify:     ca.overdue_notify || false,
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const selectedAgencyContacts = contacts.filter(
    c => c.agency === form.assigned_org_name
  );

  const handleField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleAgencySelect = (agency) => {
    const agencyContacts = contacts.filter(c => c.agency === agency);
    setForm(f => ({
      ...f,
      assigned_org_name: agency,
      assigned_contact_ids: agencyContacts.map(c => c.contact_id),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const cleanForm = { ...form };
      const gsiNullable = ['due_date', 'reminder_days', 'assigned_org_id', 'assigned_at', 'assigned_by'];
      gsiNullable.forEach(field => {
        if (cleanForm[field] === '' || cleanForm[field] === null || cleanForm[field] === undefined) {
          delete cleanForm[field];
        }
      });
      console.log('Saving CA:', ca.ca_id, 'form:', cleanForm);
      await onSave(ca.ca_id, cleanForm);
      toast.success('Corrective action saved');
      onClose();
    } catch (e) {
      console.error('handleSave error:', e);
      toast.error(`Failed to save: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!form.assigned_org_name) { toast.error('Assign an organization first'); return; }
    if (form.assigned_contact_ids.length === 0) { toast.error('No contacts for this organization'); return; }
    setSending(true);
    try {
      const cleanForm = { ...form };
      const gsiNullable = ['due_date', 'reminder_days', 'assigned_org_id', 'assigned_at', 'assigned_by'];
      gsiNullable.forEach(field => {
        if (cleanForm[field] === '' || cleanForm[field] === null || cleanForm[field] === undefined) {
          delete cleanForm[field];
        }
      });
      await onSave(ca.ca_id, cleanForm);
      await onSendNotification(ca.ca_id);
      toast.success('Notification sent');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const tabs = [
    { id: 'details',    label: 'Details' },
    { id: 'assignment', label: 'Assignment & Dates' },
    { id: 'status',     label: 'Status & Notes' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 680,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,40,85,0.18)',
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '20px 24px 0', borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <StatusBadge status={form.status} />
                <PriorityBadge priority={form.priority} />
                {ca.source && (
                  <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                    {ca.source === 'auto_gap' ? 'From capability gap' :
                     ca.source === 'auto_section5' ? 'From Section 5' : 'Manual entry'}
                  </span>
                )}
              </div>
              {ca.incident_name && (
                <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>
                  {ca.incident_name} {ca.incident_date ? `· ${formatDate(ca.incident_date)}` : ''}
                </p>
              )}
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#94a3b8', padding: 4, flexShrink: 0,
            }}>
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0 }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 16px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  color: activeTab === tab.id ? '#002855' : '#94a3b8',
                  borderBottom: activeTab === tab.id ? '2px solid #002855' : '2px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

          {/* ── Details Tab ── */}
          {activeTab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input
                  value={form.title}
                  onChange={e => handleField('title', e.target.value)}
                  style={inputStyle}
                  placeholder="Short, actionable title"
                />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={form.description}
                  onChange={e => handleField('description', e.target.value)}
                  style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
                  placeholder="Full description of the corrective action required"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>NIMS Capability</label>
                  <select value={form.nims_capability} onChange={e => handleField('nims_capability', e.target.value)} style={inputStyle}>
                    {NIMS_CAPABILITIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Resource Type</label>
                  <select value={form.resource_type} onChange={e => handleField('resource_type', e.target.value)} style={inputStyle}>
                    {POETE_CATEGORIES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Priority</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['High', 'Medium', 'Low'].map(p => (
                    <button
                      key={p}
                      onClick={() => handleField('priority', p)}
                      style={{
                        padding: '6px 20px', borderRadius: 20, fontSize: 13,
                        fontWeight: 600, cursor: 'pointer', border: '2px solid',
                        borderColor: form.priority === p ? PRIORITY_CONFIG[p].color : '#e2e8f0',
                        background: form.priority === p ? PRIORITY_CONFIG[p].bg : '#fff',
                        color: form.priority === p ? PRIORITY_CONFIG[p].color : '#64748b',
                        transition: 'all 0.15s',
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Assignment & Dates Tab ── */}
          {activeTab === 'assignment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Assigned Organization</label>
                <select
                  value={form.assigned_org_name}
                  onChange={e => handleAgencySelect(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">— Select an organization —</option>
                  {agencies.map(ag => (
                    <option key={ag} value={ag}>{ag}</option>
                  ))}
                </select>
                {form.assigned_org_name && selectedAgencyContacts.length > 0 && (
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Contacts who will receive notification
                    </p>
                    {selectedAgencyContacts.map(c => (
                      <div key={c.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <User size={11} color="#94a3b8" />
                        <span style={{ fontSize: 12, color: '#374151' }}>{c.name}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>· {c.email}</span>
                        {c.title && <span style={{ fontSize: 11, color: '#94a3b8' }}>· {c.title}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {form.assigned_org_name && selectedAgencyContacts.length === 0 && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#f97316' }}>
                    ⚠ No contacts found for this organization. Add contacts in the Contacts tab.
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle}>Due Date</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={e => handleField('due_date', e.target.value)}
                  style={inputStyle}
                />
              </div>

              {form.due_date && (
                <>
                  <div>
                    <label style={labelStyle}>Reminder Email</label>
                    <select
                      value={form.reminder_days ?? ''}
                      onChange={e => handleField('reminder_days', e.target.value ? parseInt(e.target.value) : null)}
                      style={inputStyle}
                    >
                      {REMINDER_OPTIONS.map(o => (
                        <option key={o.value ?? 'none'} value={o.value ?? ''}>{o.label}</option>
                      ))}
                    </select>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>
                      Sent automatically to assigned organization contacts
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      id="overdue_notify"
                      checked={form.overdue_notify}
                      onChange={e => handleField('overdue_notify', e.target.checked)}
                      style={{ accentColor: '#002855', width: 15, height: 15 }}
                    />
                    <label htmlFor="overdue_notify" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                      Send overdue notification (once, when due date passes)
                    </label>
                  </div>
                </>
              )}

              {/* Notification status */}
              {ca.notification_sent && (
                <div style={{
                  padding: '10px 14px', background: '#f0fdf4',
                  border: '1px solid #bbf7d0', borderRadius: 8,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <CheckCheck size={14} color="#16a34a" />
                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                    Notification sent {ca.notification_sent_at ? formatDate(ca.notification_sent_at) : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Status & Notes Tab ── */}
          {activeTab === 'status' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Status</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(STATUS_CONFIG).map(([s, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={s}
                        onClick={() => handleField('status', s)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', borderRadius: 20, fontSize: 12,
                          fontWeight: 600, cursor: 'pointer',
                          border: `2px solid ${form.status === s ? cfg.color : '#e2e8f0'}`,
                          background: form.status === s ? cfg.bg : '#fff',
                          color: form.status === s ? cfg.color : '#94a3b8',
                          transition: 'all 0.15s',
                        }}
                      >
                        <Icon size={11} /> {cfg.label}
                      </button>
                    );
                  })}
                </div>
                {(form.status === 'completed' || form.status === 'unachievable') && (
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: '#64748b' }}>
                    You can revert this to <strong>In Progress</strong> at any time by selecting it above.
                  </p>
                )}
              </div>
              <div>
                <label style={labelStyle}>Status Notes</label>
                <textarea
                  value={form.status_notes}
                  onChange={e => handleField('status_notes', e.target.value)}
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  placeholder="Add notes about the current status, progress, or blockers..."
                />
              </div>

              {/* ── Recipient Update Review ── */}
              {ca.recipient_update_status === 'pending_review' && ca.recipient_update && (
                <div style={{
                  padding: '16px', background: '#fffbeb',
                  border: '2px solid #fde68a', borderRadius: 10,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#d97706' }}>
                      ⏳ Pending Recipient Update
                    </p>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      {formatDate(ca.recipient_update.submitted_at)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                    {ca.recipient_update.recipient_name && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', minWidth: 100 }}>Submitted by:</span>
                        <span style={{ fontSize: 12, color: '#374151' }}>{ca.recipient_update.recipient_name}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', minWidth: 100 }}>Status:</span>
                      <span style={{
                        fontSize: 12, fontWeight: 700,
                        color: STATUS_CONFIG[ca.recipient_update.status]?.color || '#374151',
                      }}>
                        {STATUS_CONFIG[ca.recipient_update.status]?.label || ca.recipient_update.status}
                      </span>
                    </div>
                    {ca.recipient_update.completion_date && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', minWidth: 100 }}>Completed:</span>
                        <span style={{ fontSize: 12, color: '#374151' }}>{formatDate(ca.recipient_update.completion_date)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', minWidth: 100, flexShrink: 0 }}>Notes:</span>
                      <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{ca.recipient_update.notes}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => onAcceptUpdate(ca.ca_id)}
                      style={{
                        flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                        background: '#002855', color: '#fff', border: 'none', cursor: 'pointer',
                      }}
                    >
                      ✓ Accept Update
                    </button>
                    <button
                      onClick={() => onDismissUpdate(ca.ca_id)}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', cursor: 'pointer',
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {ca.recipient_update_status === 'accepted' && (
                <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                    ✓ Recipient update was accepted and applied.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
            background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}>
            Cancel
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            {/* Send Notification button — only show if assigned and not yet sent or want to resend */}
            {form.assigned_org_name && form.assigned_contact_ids.length > 0 && (
              <button
                onClick={handleSend}
                disabled={sending || saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: '1px solid #F97316', background: '#fff', color: '#F97316',
                  cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {sending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
                {ca.notification_sent ? 'Resend Notification' : 'Save & Send Notification'}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: saving ? '#94a3b8' : '#002855', color: '#fff',
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── New CA Modal ──────────────────────────────────────────────────────────────

const NewCAModal = ({ agencies, contacts, organization, onClose, onCreate }) => {
  const [form, setForm] = useState({
    title: '', description: '', nims_capability: 'General',
    resource_type: 'Planning', priority: 'Medium',
    assigned_org_name: '', assigned_contact_ids: [],
    due_date: '', reminder_days: null, overdue_notify: false,
  });
  const [saving, setSaving] = useState(false);

  const handleField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const handleAgencySelect = (agency) => {
    const agencyContacts = contacts.filter(c => c.agency === agency);
    setForm(f => ({
      ...f, assigned_org_name: agency,
      assigned_contact_ids: agencyContacts.map(c => c.contact_id),
    }));
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      await onCreate(form);
      toast.success('Corrective action created');
      onClose();
    } catch { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 580,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,40,85,0.18)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
              New Corrective Action
            </h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Title *</label>
            <input value={form.title} onChange={e => handleField('title', e.target.value)} style={inputStyle} placeholder="Short, actionable title" />
          </div>
          <div>
            <label style={labelStyle}>Description *</label>
            <textarea value={form.description} onChange={e => handleField('description', e.target.value)} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="What needs to be done?" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>NIMS Capability</label>
              <select value={form.nims_capability} onChange={e => handleField('nims_capability', e.target.value)} style={inputStyle}>
                {NIMS_CAPABILITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={form.priority} onChange={e => handleField('priority', e.target.value)} style={inputStyle}>
                {['High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Assigned Organization</label>
            <select value={form.assigned_org_name} onChange={e => handleAgencySelect(e.target.value)} style={inputStyle}>
              <option value="">— Optional —</option>
              {agencies.map(ag => <option key={ag} value={ag}>{ag}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Due Date</label>
            <input type="date" value={form.due_date} onChange={e => handleField('due_date', e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: saving ? '#94a3b8' : '#002855', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? <Loader2 size={14} /> : <Plus size={14} />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Contacts Panel ────────────────────────────────────────────────────────────

const ContactsPanel = ({ contacts, organization, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', title: '', agency: '', email: '', phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleCreate = async () => {
    if (!form.agency) { toast.error('Organization name is required'); return; }
    if (form.name && !form.email) { toast.error('Email is required when adding a named contact'); return; }
    setSaving(true);
    try {
      const url = `${API_ENDPOINT}/dashboard/contacts?organization_id=${organization.organizationId}`;
      await authenticatedFetch(url, { method: 'POST', body: JSON.stringify(form) });
      toast.success(form.name ? 'Contact added' : 'Organization added');
      setForm({ name: '', title: '', agency: '', email: '', phone: '', notes: '' });
      setShowAdd(false);
      onRefresh();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Remove ${contact.name || contact.agency} from contacts?`)) return;
    setDeleting(contact.contact_id);
    try {
      const url = `${API_ENDPOINT}/dashboard/contacts/${contact.contact_id}?organization_id=${organization.organizationId}`;
      await authenticatedFetch(url, { method: 'DELETE' });
      toast.success('Contact removed');
      onRefresh();
    } catch { toast.error('Failed to remove contact'); }
    finally { setDeleting(null); }
  };

  // Group by agency
  const byAgency = contacts.reduce((acc, c) => {
    if (!acc[c.agency]) acc[c.agency] = [];
    acc[c.agency].push(c);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
          Contacts receive notification emails when assigned a corrective action.
        </p>
        <button
          onClick={() => setShowAdd(s => !s)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: '#002855', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          <Plus size={14} /> Add Contact
        </button>
      </div>

      {showAdd && (
        <div style={{ padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 16 }}>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b' }}>
            You can add an organization without a contact person, or add a named contact with an email address.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div><label style={labelStyle}>Agency / Organization *</label><input value={form.agency} onChange={e => setForm(f => ({ ...f, agency: e.target.value }))} style={inputStyle} placeholder="Organization name" /></div>
            <div><label style={labelStyle}>Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="Contact person (optional)" /></div>
            <div><label style={labelStyle}>Email {form.name ? '*' : ''}</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} placeholder={form.name ? 'Required if name provided' : 'Optional'} /></div>
            <div><label style={labelStyle}>Title</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Job title (optional)" /></div>
            <div><label style={labelStyle}>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inputStyle} placeholder="Optional" /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={handleCreate} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: '#002855', color: '#fff', border: 'none', cursor: 'pointer' }}>
              {saving ? <Loader2 size={13} /> : <Plus size={13} />} Save Contact
            </button>
          </div>
        </div>
      )}

      {Object.keys(byAgency).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
          <User size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No contacts yet</p>
          <p style={{ margin: '4px 0 0', fontSize: 12 }}>Add contacts to assign corrective actions to organizations</p>
        </div>
      ) : (
        Object.entries(byAgency).sort(([a], [b]) => a.localeCompare(b)).map(([agency, agencyContacts]) => (
          <div key={agency} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, padding: '6px 10px', background: '#EBF0F7', borderRadius: 6 }}>
              <Building2 size={13} color="#002855" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#002855' }}>{agency}</span>
              <span style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto' }}>{agencyContacts.length} contact{agencyContacts.length !== 1 ? 's' : ''}</span>
            </div>
            {agencyContacts.map(c => (
              <div key={c.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', marginBottom: 4 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: c.name ? '#002855' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {c.name
                    ? <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{c.name[0]?.toUpperCase()}</span>
                    : <Building2 size={14} color="#94a3b8" />
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                    {c.name || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No contact person</span>}
                  </p>
                  <p style={{ margin: 0, fontSize: 11, color: '#64748b' }}>
                    {c.title && `${c.title} · `}
                    {c.email || <span style={{ color: '#cbd5e1' }}>No email</span>}
                    {c.phone && ` · ${c.phone}`}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(c)}
                  disabled={deleting === c.contact_id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 4, borderRadius: 4 }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}
                >
                  {deleting === c.contact_id ? <Loader2 size={14} /> : <Trash2 size={14} />}
                </button>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 700, color: '#374151',
  marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4,
};

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 7,
  border: '1.5px solid #e2e8f0', fontSize: 13, color: '#0f172a',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
  fontFamily: 'inherit',
};

// ── Main View ─────────────────────────────────────────────────────────────────

export default function CorrectiveActionsView() {
  const { organization } = useContext(AuthContext);

  const [cas, setCAs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');  // open | in_progress | draft | completed | unachievable
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Filters
  const [search, setSearch]         = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterOrg, setFilterOrg]           = useState('');
  const [filterDue, setFilterDue]           = useState('');
  const [showFilters, setShowFilters]       = useState(false);

  // Modals
  const [editCA, setEditCA]           = useState(null);
  const [showNew, setShowNew]         = useState(false);
  const [showContacts, setShowContacts] = useState(false);

  const loadData = useCallback(async () => {
    if (!organization?.organizationId) return;
    setLoading(true);
    try {
      const [caRes, contactRes] = await Promise.all([
        fetchWithAuth('/dashboard/corrective-actions', organization.organizationId),
        fetchWithAuth('/dashboard/contacts', organization.organizationId),
      ]);
      setCAs(caRes.corrective_actions || []);
      setContacts(contactRes.contacts || []);
    } catch (e) {
      toast.error('Failed to load corrective actions');
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => { loadData(); }, [loadData]);

  // Derived
  const agencies = [...new Set(contacts.map(c => c.agency))].sort();

  const filtered = cas.filter(ca => {
    if (filterPriority && ca.priority !== filterPriority) return false;
    if (filterOrg && ca.assigned_org_name !== filterOrg) return false;
    if (filterDue) {
      if (filterDue === 'overdue' && !isOverdue(ca.due_date, ca.status)) return false;
      if (filterDue === '7days') {
        const d = daysUntilDue(ca.due_date);
        if (d === null || d < 0 || d > 7) return false;
      }
      if (filterDue === '30days') {
        const d = daysUntilDue(ca.due_date);
        if (d === null || d < 0 || d > 30) return false;
      }
    }
    if (search) {
      const q = search.toLowerCase();
      return (
        ca.title?.toLowerCase().includes(q) ||
        ca.description?.toLowerCase().includes(q) ||
        ca.nims_capability?.toLowerCase().includes(q) ||
        ca.assigned_org_name?.toLowerCase().includes(q) ||
        ca.incident_name?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleUpdate = async (ca_id, form) => {
    console.log('handleUpdate called, ca_id:', ca_id, 'status:', form.status);
    try {
      const url = `${API_ENDPOINT}/dashboard/corrective-actions/${ca_id}?organization_id=${organization.organizationId}`;
      const res = await authenticatedFetch(url, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      console.log('PUT response:', res);
      console.log('Saved status:', res?.status, '| corrective_action status:', res?.corrective_action?.status);
    } catch (e) {
      console.error('handleUpdate fetch error:', e);
      throw e;
    }
    if (form.status && STATUS_ORDER.includes(form.status)) {
      setActiveTab(form.status);
      setCurrentPage(1);
    }
    await loadData();
  };

  const handleCreate = async (form) => {
    const url = `${API_ENDPOINT}/dashboard/corrective-actions?organization_id=${organization.organizationId}`;
    await authenticatedFetch(url, { method: 'POST', body: JSON.stringify(form) });
    await loadData();
  };

  const handleDelete = async (ca) => {
    if (!window.confirm(`Delete "${ca.title}"? This cannot be undone.`)) return;
    try {
      const url = `${API_ENDPOINT}/dashboard/corrective-actions/${ca.ca_id}?organization_id=${organization.organizationId}`;
      await authenticatedFetch(url, { method: 'DELETE' });
      toast.success('Deleted');
      await loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSendNotification = async (ca_id) => {
    const url = `${API_ENDPOINT}/dashboard/corrective-actions/${ca_id}/send-notification?organization_id=${organization.organizationId}`;
    await authenticatedFetch(url, { method: 'POST' });
    await loadData();
  };

  const handleAcceptUpdate = async (ca_id) => {
    try {
      const url = `${API_ENDPOINT}/dashboard/corrective-actions/${ca_id}/accept-update?organization_id=${organization.organizationId}`;
      await authenticatedFetch(url, { method: 'POST' });
      toast.success('Recipient update accepted');
      setEditCA(null);
      await loadData();
    } catch { toast.error('Failed to accept update'); }
  };

  const handleDismissUpdate = async (ca_id) => {
    if (!window.confirm('Dismiss this recipient update without applying it?')) return;
    try {
      const url = `${API_ENDPOINT}/dashboard/corrective-actions/${ca_id}/dismiss-update?organization_id=${organization.organizationId}`;
      await authenticatedFetch(url, { method: 'POST' });
      toast.success('Update dismissed');
      setEditCA(null);
      await loadData();
    } catch { toast.error('Failed to dismiss update'); }
  };

  const assignedOrgs = [...new Set(cas.map(c => c.assigned_org_name).filter(Boolean))].sort();
  const hasFilters = filterPriority || filterOrg || filterDue || search;
  const counts = STATUS_ORDER.reduce((acc, s) => { acc[s] = cas.filter(ca => ca.status === s).length; return acc; }, {});

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0', flexDirection: 'column', gap: 12 }}>
        <Loader2 size={28} color="#002855" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>Loading corrective actions...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return (
    <>
    <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    <Widget
      title="Corrective Actions"
      subtitle={`${cas.length} total · ${counts.open || 0} open · ${counts.in_progress || 0} in progress · ${counts.draft || 0} draft · ${counts.completed || 0} completed`}
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowContacts(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center space-x-2"
          >
            <User size={15} /><span>Contacts</span>
          </button>
          <button
            onClick={() => setShowNew(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
          >
            <Plus size={15} /><span>New Corrective Action</span>
          </button>
        </div>
      }
    >

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { id: 'open',         label: 'Open',         count: counts.open || 0 },
          { id: 'in_progress',  label: 'In Progress',  count: counts.in_progress || 0 },
          { id: 'draft',        label: 'Draft',        count: counts.draft || 0 },
          { id: 'completed',    label: 'Completed',    count: counts.completed || 0 },
          { id: 'unachievable', label: 'Unachievable', count: counts.unachievable || 0 },
        ].map(tab => {
          const cfg = STATUS_CONFIG[tab.id];
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setCurrentPage(1); }}
              style={{
                padding: '10px 18px', background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 13, fontWeight: 700,
                color: isActive ? (cfg?.color || '#002855') : '#94a3b8',
                borderBottom: isActive ? `2px solid ${cfg?.color || '#002855'}` : '2px solid transparent',
                marginBottom: -2, transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              {tab.label}
              <span style={{
                fontSize: 11, fontWeight: 700,
                background: isActive ? (cfg?.bg || '#EBF0F7') : '#f1f5f9',
                color: isActive ? (cfg?.color || '#002855') : '#94a3b8',
                padding: '1px 7px', borderRadius: 10,
              }}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Status Tabs — Open / In Progress / Draft / Completed / Unachievable */}
      <>
          {/* Search + Filter Bar */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
              <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                placeholder="Search corrective actions..."
                style={{ ...inputStyle, paddingLeft: 32 }}
              />
            </div>
            <button
              onClick={() => setShowFilters(s => !s)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                border: `1.5px solid ${hasFilters ? '#002855' : '#e2e8f0'}`,
                background: hasFilters ? '#EBF0F7' : '#fff',
                color: hasFilters ? '#002855' : '#64748b', cursor: 'pointer',
              }}
            >
              <SlidersHorizontal size={14} />
              Filters {hasFilters && `(${[filterPriority, filterOrg, filterDue].filter(Boolean).length})`}
            </button>
            {hasFilters && (
              <button
                onClick={() => { setFilterPriority(''); setFilterOrg(''); setFilterDue(''); setSearch(''); setCurrentPage(1); }}
                style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', cursor: 'pointer' }}
              >
                Clear all
              </button>
            )}
          </div>

          {showFilters && (
            <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
              <div>
                <label style={labelStyle}>Priority</label>
                <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={inputStyle}>
                  <option value="">All priorities</option>
                  {['High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Organization</label>
                <select value={filterOrg} onChange={e => setFilterOrg(e.target.value)} style={inputStyle}>
                  <option value="">All organizations</option>
                  {assignedOrgs.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Due Date</label>
                <select value={filterDue} onChange={e => setFilterDue(e.target.value)} style={inputStyle}>
                  <option value="">Any due date</option>
                  <option value="overdue">Overdue</option>
                  <option value="7days">Due within 7 days</option>
                  <option value="30days">Due within 30 days</option>
                </select>
              </div>
            </div>
          )}

          {/* CA List for active status tab */}
          {(() => {
            const tabCAs = filtered.filter(ca => ca.status === activeTab);
            const totalPages = Math.ceil(tabCAs.length / ITEMS_PER_PAGE);
            const paginated = tabCAs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
            const cfg = STATUS_CONFIG[activeTab];

            if (tabCAs.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                  <ClipboardList size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: '#475569' }}>
                    {hasFilters ? 'No corrective actions match your filters' : `No ${cfg?.label.toLowerCase()} corrective actions`}
                  </p>
                  <p style={{ margin: 0, fontSize: 13 }}>
                    {hasFilters ? 'Try adjusting your filters' : 'Generate AARs to auto-populate corrective actions, or create one manually'}
                  </p>
                </div>
              );
            }

            return (
              <>
                <div style={{ marginBottom: 12, fontSize: 12, color: '#94a3b8' }}>
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, tabCAs.length)} of {tabCAs.length}
                </div>

                {paginated.map(ca => (
                  <CACard key={ca.ca_id} ca={ca} onEdit={setEditCA} onDelete={handleDelete} />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                        border: '1.5px solid #e2e8f0', background: '#fff',
                        color: currentPage === 1 ? '#cbd5e1' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          width: 32, height: 32, borderRadius: 6, fontSize: 13, fontWeight: 600,
                          border: `1.5px solid ${currentPage === page ? cfg?.color || '#002855' : '#e2e8f0'}`,
                          background: currentPage === page ? (cfg?.bg || '#EBF0F7') : '#fff',
                          color: currentPage === page ? (cfg?.color || '#002855') : '#374151',
                          cursor: 'pointer',
                        }}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                        border: '1.5px solid #e2e8f0', background: '#fff',
                        color: currentPage === totalPages ? '#cbd5e1' : '#374151',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </>

      {/* Edit Modal */}
      {editCA && (
        <CAModal
          ca={editCA}
          contacts={contacts}
          agencies={agencies}
          organization={organization}
          onClose={() => setEditCA(null)}
          onSave={handleUpdate}
          onSendNotification={handleSendNotification}
          onAcceptUpdate={handleAcceptUpdate}
          onDismissUpdate={handleDismissUpdate}
        />
      )}

      {/* New CA Modal */}
      {showNew && (
        <NewCAModal
          agencies={agencies}
          contacts={contacts}
          organization={organization}
          onClose={() => setShowNew(false)}
          onCreate={handleCreate}
        />
      )}

      {/* Contacts Modal */}
      {showContacts && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 620,
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,40,85,0.18)',
          }}>
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                  Contacts
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>
                  Organizations and contacts for corrective action assignments
                </p>
              </div>
              <button
                onClick={() => setShowContacts(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              <ContactsPanel
                contacts={contacts}
                organization={organization}
                onRefresh={loadData}
              />
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowContacts(false)}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Widget>
    </>
  );
}